import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { t } from 'i18next';

import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/Auth';
import { useMembership } from '../../context/Membership';

import { AccountNav } from '../Account/AccountNav';
import Checkout from './Checkout';
import { Modal } from '../../components/Modal';
import { IMembershipPlan, IWidget } from '../../types/model';
import { LIST_WIDGETS } from '../../http/apollo-queries';
import { formatDate } from '../../helpers/functions';
import { Button, Card, Icon, Loading, Table } from '../../components/UI';
import SwagBag from './SwagBag';
import { logger } from '../../helpers/logger';

type CollapsePropTypes = {
  widgets: [IWidget];
  isOpen?: boolean;
  onClick: () => void;
};

type CollapseHeaderColumnPropTypes = CollapsePropTypes & {
  membershipPlans: IMembershipPlan[];
};

type CollapseBodyColumnPropTypes = CollapsePropTypes & {
  membershipPlan: IMembershipPlan;
  isLastColumn?: boolean;
};

// first column component for each widget category
const CollapseHeaderColumn: React.FC<CollapseHeaderColumnPropTypes> = ({
  widgets,
  onClick,
  isOpen,
}) => {
  const { t } = useTranslation();

  if (!widgets.length) {
    return null;
  }

  return (
    <>
      <div
        className={`
          flex
          items-center
          min-h-20
          text-sm
          py-2
          border-t
          border-grey-10
          ${!isOpen ? 'dark:bg-black-real bg-grey-5' : ''}
          cursor-pointer
        `}
        onClick={onClick}
      >
        <div className={`flex items-center pl-3 h-full w-full`}>
          <div className="flex flex-col w-full">
            <span className="font-semibold">
              {`"${widgets[0].category}" ${t('subscriptions.widgets')}`}
            </span>
          </div>
        </div>
      </div>

      {isOpen &&
        widgets.map((widget) => (
          <div
            key={widget.id}
            className="flex items-center text-left text-sm pl-4 min-h-20 py-2"
          >
            {t(widget.name)}
          </div>
        ))}
    </>
  );
};

const CollapseBodyColumn: React.FC<CollapseBodyColumnPropTypes> = ({
  widgets,
  membershipPlan,
  isOpen,
  onClick,
  isLastColumn,
}) => {
  if (!widgets.length) {
    return null;
  }

  const countAvailableWidgets = widgets.reduce((acc, widget) => {
    if (
      widget.membershipPlans.items.findIndex(
        (plan) => plan.membershipPlan.id === membershipPlan.id
      ) !== -1
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);

  return (
    <>
      <div
        className={`
        relative
          flex
          min-h-20
          py-2
          border-t
          border-grey-10
          justify-center
          items-center
          ${!isOpen ? 'dark:bg-black-real bg-grey-5' : ''}
          cursor-pointer`}
        onClick={onClick}
      >
        {countAvailableWidgets}

        {isLastColumn && (
          <div className={`absolute right-0`}>
            <Icon svgName={isOpen ? 'arrowUp' : 'arrowDown'} className="m-5" />
          </div>
        )}
      </div>

      <div className={'text-center'}>
        {isOpen &&
          widgets.map((widget) => (
            <div
              className={
                'flex items-center min-h-20 text-sm py-2 justify-center'
              }
              key={`${membershipPlan.id}-${widget.id}`}
            >
              {widget.membershipPlans.items.findIndex(
                (plan) => plan.membershipPlan.id === membershipPlan.id
              ) !== -1 ? (
                <Icon
                  svgName={'check'}
                  className={'mx-auto w-5 h-5 bg-lime rounded-full p-1'}
                />
              ) : (
                <Icon
                  svgName={'check'}
                  className={
                    'mx-auto w-5 h-5 bg-grey-30 rounded-full p-1 text-white'
                  }
                />
              )}
            </div>
          ))}
      </div>
    </>
  );
};

const Subscriptions = () => {
  const { user } = useAuth();
  const { membershipPlans } = useMembership();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSwagBagModalOpen, setIsSwagBagModalOpen] = useState(false);
  const [currentMembershipPlan, setCurrentMembershipPlan] = useState(
    {} as IMembershipPlan
  );
  const [widgetCategoriesOpen, setWidgetCategoriesOpen] = useState<{
    [k: string]: boolean;
  }>({});

  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState('');
  const [checkoutAction, setCheckoutAction] = useState<
    'upgrade' | 'downgrade' | 'same'
  >('upgrade');

  const {
    data: { listWidgets: widgets = [], loading: listWidgetsLoading } = {},
  } = useQuery(LIST_WIDGETS);

  useEffect(() => {
    const currentMembershipPlan = membershipPlans.find(
      (m) =>
        (!user?.membership.membershipPlan.id && m.default) ||
        user!.membership.membershipPlan.id === m.id
    );

    setCurrentMembershipPlan(currentMembershipPlan!);
    logger.log('Checkout:currentMembershipPlan', currentMembershipPlan);
  }, [membershipPlans, user?.membership, user]);

  const nextBillingDate = moment(user?.membership.updatedAt)
    .add(1, user?.membership.renewal === 'MONTHLY' ? 'M' : 'y')
    .format('YYYY/MM/DD');

  const widgetsByCategories = useMemo(() => {
    if (!widgets || !widgets.items) {
      return [];
    }

    const byCategories: { [id: string]: [IWidget] } = {};

    [...widgets.items]
      .sort(
        ({ position: position1 }, { position: position2 }) =>
          position1 - position2
      )
      .forEach((widget: IWidget) => {
        byCategories[widget.category] = byCategories[widget.category] || [];

        byCategories[widget.category].push(widget);
      });

    return byCategories;
  }, [widgets]);

  const columnFeatures = Object.keys(membershipPlans[0]?.features || {}).filter(
    (f) => f !== '__typename'
  );
  const strategiesIndex = columnFeatures.indexOf('strategies');

  return (
    <div className="flex flex-col w-full bg-white text-black items-center dark:bg-black-real dark:text-white">
      <div className="w-full lg:w-2/3X">
        <AccountNav />

        {isModalOpen && (
          <Modal
            className="h-full w-full"
            handleMerlinLogo={true}
            customMerlinLogoColor={'#000000'}
            element={
              <div className={'flex flex-col w-full h-full bg-white'}>
                <div
                  className={`h-16 ${
                    checkoutAction === 'upgrade' ? 'bg-lime' : 'bg-grey-10'
                  } items-center flex justify-end p-4`}
                >
                  <Icon
                    className={'cursor-pointer fill-black'}
                    svgName={'close'}
                    onClick={() => setIsModalOpen(false)}
                  />
                </div>
                <Checkout
                  membershipPlanId={selectedMembershipPlanId}
                  action={checkoutAction}
                  setIsModalOpen={setIsModalOpen}
                />
              </div>
            }
            isFullScreenElement={true}
            isOpen={true}
          />
        )}
        {isSwagBagModalOpen && (
          <Modal
            className="h-full w-full"
            handleMerlinLogo={true}
            customMerlinLogoColor={'#000000'}
            element={
              <div className={'flex flex-col w-full h-full bg-white'}>
                <div className={'h-16 flex justify-end p-4'}>
                  <Icon
                    className={'cursor-pointer'}
                    svgName={'close'}
                    onClick={() => setIsSwagBagModalOpen(false)}
                  />
                </div>
                <SwagBag
                  membershipPlanId={selectedMembershipPlanId}
                  action={checkoutAction}
                  setIsModalOpen={setIsSwagBagModalOpen}
                />
              </div>
            }
            isFullScreenElement={true}
            isOpen={true}
          />
        )}

        <div className="flex flex-col">
          {!membershipPlans.length ||
          !currentMembershipPlan?.id ||
          listWidgetsLoading ? (
            <Loading status={true} className={'mx-auto mt-16'} />
          ) : (
            <>
              {/* CURRENT MEMBERSHIP */}
              <Card
                className="mt-4"
                padding={false}
                title={t('subscriptions.myPlan')}
              >
                <Table
                  columns={[
                    t('subscriptions.currentPlan'),
                    t('subscriptions.price'),
                    t('subscriptions.activatedAt'),
                    t('subscriptions.renewalAt'),
                  ]}
                  data={[
                    [
                      currentMembershipPlan.title,
                      t('subscriptions.priceRenewal', {
                        price: user?.membership.price,
                      }) +
                        '/' +
                        t('subscriptions.' + user?.membership.renewal),
                      formatDate(user!.membership.updatedAt!),
                      nextBillingDate,
                    ],
                  ]}
                />
              </Card>

              {/* MOBILE MEMBERSHIP WARNING */}
              {user?.membership?.paid &&
                user?.membership.source === 'MOBILE' && (
                  <Card className="my-4 text-center">
                    {t('subscriptions.mobileWarning')}
                  </Card>
                )}

              {/* SUBSCRIPTIONS */}
              <Card className="my-4" padding={false}>
                {/* MEMBERSHIP HEADER */}
                <Table
                  columns={['', ...membershipPlans.map((m) => m.title)]}
                  textAlign={'center'}
                />

                {/* MEMBERSHIP CONTENTS */}
                <div className="flex flex-col w-full">
                  <div className={'inline-grid grid-cols-5 w-full'}>
                    {/* MEMBERSHIP FEATURES */}
                    <div>
                      {['', ...columnFeatures].map((feature, index) => (
                        <React.Fragment key={`${feature}-${index}`}>
                          <div
                            className={`
                              flex
                              items-center
                              text-left
                              text-sm
                              font-semibold
                              pl-4
                              border-grey-10
                              ${
                                index === 0
                                  ? 'min-h-32'
                                  : 'min-h-20 py-2 border-t '
                              }
                            `}
                          >
                            {feature ? (
                              feature === 'swagBag' ? (
                                <div>
                                  {' '}
                                  {t('checkout.features.label.' + feature)}{' '}
                                  <div
                                    className={`items-center rounded-lg text-sm py-1 px-2 mt-1 bg-lime`}
                                  >
                                    {t('subscriptions.yearlyPlanOnly')}
                                  </div>
                                </div>
                              ) : (
                                t('checkout.features.label.' + feature)
                              )
                            ) : (
                              ''
                            )}
                          </div>

                          {feature === 'strategies' &&
                            Object.keys(widgetsByCategories).map((category) => (
                              <div key={`${category}-header`}>
                                <CollapseHeaderColumn
                                  isOpen={widgetCategoriesOpen[category]}
                                  onClick={() =>
                                    setWidgetCategoriesOpen((prevState) => ({
                                      ...prevState,
                                      [category]: !prevState[category],
                                    }))
                                  }
                                  widgets={widgetsByCategories[category]}
                                  membershipPlans={membershipPlans}
                                />
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* MEMBERSHIP ITEMS */}
                    {membershipPlans.map(
                      (membershipPlan, membershipPlanIndex) => (
                        <React.Fragment key={membershipPlan.id}>
                          <div className={'text-center'}>
                            {/* MEMBERSHIP PRICE + CTA */}
                            <div
                              className={`flex flex-col items-start min-h-32 border-grey-10 text-center`}
                            >
                              <div className={'flex flex-col mx-auto my-4'}>
                                <div className={'min-h-8'}>
                                  {membershipPlan.prices
                                    .filter(
                                      (p) =>
                                        p.renewal === user?.membership.renewal
                                    )
                                    .map((price, index) => (
                                      <div
                                        key={`${membershipPlan.id}-${price.sku}`}
                                        className={`${
                                          index > 0
                                            ? 'text-sm'
                                            : 'text-sm font-semibold'
                                        }`}
                                      >
                                        {index === 0 || price.price > 0
                                          ? t('subscriptions.priceRenewal', {
                                              price: price.price,
                                            }) +
                                            '/' +
                                            t('subscriptions.' + price.renewal)
                                          : ''}
                                      </div>
                                    ))}
                                </div>

                                <div>
                                  <Button
                                    variant={
                                      membershipPlan.id ===
                                      currentMembershipPlan.id
                                        ? 'secondary'
                                        : 'primary'
                                    }
                                    disabled={
                                      (user?.membership?.paid &&
                                        user?.membership?.source ===
                                          'MOBILE') ||
                                      (membershipPlan.id ===
                                        membershipPlans[0].id &&
                                        currentMembershipPlan.id ===
                                          membershipPlan.id)
                                    }
                                    onClick={() => {
                                      setSelectedMembershipPlanId(
                                        membershipPlan.id
                                      );
                                      setIsModalOpen(true);
                                      setCheckoutAction(
                                        membershipPlan.prices[0].sku ===
                                          currentMembershipPlan.prices[0].sku
                                          ? 'same'
                                          : membershipPlan.prices[0].price >
                                            currentMembershipPlan.prices[0]
                                              .price
                                          ? 'upgrade'
                                          : 'downgrade'
                                      );
                                    }}
                                  >
                                    {membershipPlan.id ===
                                      membershipPlans[0].id &&
                                    membershipPlan.id ===
                                      currentMembershipPlan.id
                                      ? t('subscriptions.yourPlan')
                                      : membershipPlan.prices[0].sku ===
                                        currentMembershipPlan.prices[0].sku
                                      ? t('subscriptions.switchTo') +
                                        (user?.membership.renewal === 'YEARLY'
                                          ? t('subscriptions.monthly')
                                          : t('subscriptions.yearly'))
                                      : membershipPlan.prices[0].price >
                                        currentMembershipPlan.prices[0].price
                                      ? t('subscriptions.upgrade')
                                      : t('subscriptions.downgrade')}
                                  </Button>

                                  <div
                                    className="text-grey-50 text-xs mt-2 cursor-pointer"
                                    onClick={() => {
                                      setSelectedMembershipPlanId(
                                        membershipPlan.id
                                      );
                                      setIsModalOpen(true);
                                      setCheckoutAction(
                                        membershipPlan.prices[0].price >
                                          currentMembershipPlan.prices[0].price
                                          ? 'upgrade'
                                          : 'downgrade'
                                      );
                                    }}
                                  >
                                    {t('subscriptions.moreInfo')}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* MEMBERSHIP FEATURES */}
                            <div>
                              {columnFeatures.map((feature, index) => (
                                <React.Fragment key={feature}>
                                  {index === strategiesIndex + 1 &&
                                    Object.keys(widgetsByCategories).map(
                                      (category) => (
                                        <CollapseBodyColumn
                                          key={`${category}-${membershipPlan.id}`}
                                          isOpen={
                                            widgetCategoriesOpen[category]
                                          }
                                          isLastColumn={
                                            membershipPlanIndex ===
                                            membershipPlans.length - 1
                                          }
                                          onClick={() =>
                                            setWidgetCategoriesOpen(
                                              (prevState) => ({
                                                ...prevState,
                                                [category]:
                                                  !prevState[category],
                                              })
                                            )
                                          }
                                          widgets={
                                            widgetsByCategories[category]
                                          }
                                          membershipPlan={membershipPlan}
                                        />
                                      )
                                    )}
                                  <div
                                    className={
                                      'flex items-center min-h-20 text-sm py-2 border-t border-grey-10 justify-center'
                                    }
                                    key={`${membershipPlan.id}-${feature}`}
                                  >
                                    {membershipPlan?.features[feature] ===
                                      true && feature !== 'swagBag' ? (
                                      <Icon
                                        svgName={'check'}
                                        className={
                                          'mx-auto w-5 h-5 bg-lime rounded-full p-1'
                                        }
                                      />
                                    ) : isNaN(
                                        membershipPlan?.features[feature]
                                      ) && feature !== 'swagBag' ? (
                                      t(
                                        'checkout.features.value.' +
                                          membershipPlan?.features[feature]
                                      )
                                    ) : membershipPlan?.features[feature] ===
                                        -1 && feature !== 'swagBag' ? (
                                      t('checkout.features.value.unlimited')
                                    ) : membershipPlan?.features[feature] ===
                                        0 ||
                                      (membershipPlan?.features[feature] ===
                                        false &&
                                        feature !== 'swagBag') ? (
                                      <Icon
                                        svgName={'check'}
                                        className={
                                          'mx-auto w-5 h-5 bg-grey-30 rounded-full p-1 text-white'
                                        }
                                      />
                                    ) : feature === 'swagBag' ? (
                                      <div
                                        className="font-semibold cursor-pointer"
                                        onClick={() => {
                                          setSelectedMembershipPlanId(
                                            membershipPlan.id
                                          );
                                          setIsSwagBagModalOpen(true);
                                          setCheckoutAction(
                                            membershipPlan.prices[0].price >
                                              currentMembershipPlan.prices[0]
                                                .price
                                              ? 'upgrade'
                                              : 'downgrade'
                                          );
                                        }}
                                      >
                                        {Array.from(
                                          membershipPlan?.features[feature]
                                        ).length === 0 ? (
                                          <Icon
                                            svgName={'check'}
                                            className={
                                              'mx-auto w-5 h-5 bg-grey-30 rounded-full p-1 text-white'
                                            }
                                          />
                                        ) : (
                                          t('subscriptions.viewPack')
                                        )}
                                      </div>
                                    ) : (
                                      membershipPlan?.features[feature]
                                    )}
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </React.Fragment>
                      )
                    )}
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
